import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createShopifyCart, api, trackKlaviyo } from "@/lib/api";
import { trackBeginCheckout } from "@/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, clear, addItem, markPurchased } = useCart();
  const [loading, setLoading] = React.useState(false);

  const excludeIds = items.map((i) => i.product.id).join(",");
  const { data: upsell = [] } = useQuery({
    queryKey: ["upsell", excludeIds],
    queryFn: async () => {
      const { data } = await api.get("/products/upsell", { params: { exclude: excludeIds, limit: 4 } });
      return data;
    },
    enabled: isOpen && items.length > 0,
    staleTime: 60_000,
  });

  const goCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    // Klaviyo: Started Checkout for abandoned-cart flow (only fires if email is known)
    trackBeginCheckout(items, subtotal);
    trackKlaviyo("Started Checkout", {
      subtotal,
      item_count: items.reduce((s, i) => s + i.quantity, 0),
      items: items.map((i) => ({
        product_id: i.product.id,
        product_name: i.product.name,
        brand: i.product.brand,
        price: i.variant.price,
        quantity: i.quantity,
      })),
    });
    try {
      let discountCode = null;
      try {
        discountCode = localStorage.getItem("doria_pending_discount");
      } catch (e) {
        // ignore
      }
      const cart = await createShopifyCart(
        items.map((i) => ({ variant_id: i.variant.id, quantity: i.quantity })),
        discountCode
      );
      if (cart.checkoutUrl) {
        markPurchased();
        try {
          localStorage.removeItem("doria_pending_discount");
          localStorage.setItem("doria_checkout_pending", Date.now().toString());
          // Save purchased items for Thank You page recommendations
          localStorage.setItem("doria_last_purchase", JSON.stringify(
            items.map((i) => ({ name: i.product.name, brand: i.product.brand, tags: i.product.tags || [], image: i.product.image, price: i.variant.price }))
          ));
        } catch (e) {
          // ignore
        }
        // Redirect FIRST — cart clears on next visit via checkout_pending flag
        window.location.href = cart.checkoutUrl;
      } else {
        toast.error("Couldn't create checkout — please try again");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-obsidian/30 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-bone z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="p-6 border-b border-black/10 flex items-center justify-between">
              <h2 className="font-serif text-2xl">Your <span className="italic">bag</span></h2>
              <button onClick={() => setIsOpen(false)} data-testid="cart-close" className="p-1"><X className="w-4 h-4" /></button>
            </div>

            {/* Free samples progress bar */}
            {items.length > 0 && (() => {
              const threshold = 75;
              const remaining = Math.max(0, threshold - subtotal);
              const pct = Math.min(100, (subtotal / threshold) * 100);
              return (
                <div className="px-6 py-4 border-b border-black/10 bg-[#0A0A0A]/5" data-testid="cart-samples-bar">
                  {remaining > 0 ? (
                    <p className="text-xs font-sans text-obsidian mb-2 leading-snug">
                      Add <span className="font-serif italic text-[#0A0A0A]">${remaining.toFixed(2)}</span> more for <span className="font-serif italic">free shipping + a complimentary sample</span>.
                    </p>
                  ) : (
                    <p className="text-xs font-sans text-obsidian mb-2 leading-snug">
                      <span className="font-serif italic text-[#0A0A0A]">Unlocked</span> — free shipping and complimentary sample included.
                    </p>
                  )}
                  <div className="h-1 bg-black/10 overflow-hidden">
                    <div
                      className="h-full bg-[#0A0A0A] transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 && (
                <div className="text-center py-16" data-testid="cart-empty">
                  <p className="font-serif italic text-xl text-neutral-600">Your bag awaits.</p>
                  <p className="text-sm text-neutral-500 mt-2 font-sans">Add pieces you love.</p>
                </div>
              )}

              {items.map((it) => (
                <div key={it.variant.id} className="flex gap-4" data-testid={`cart-item-${it.product.id}`}>
                  {it.product.image && <img src={it.product.image} alt="" className="w-20 h-24 object-cover" />}
                  <div className="flex-1 flex flex-col">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-sans">{it.product.brand}</p>
                    <p className="font-serif text-lg leading-tight mt-1">{it.product.name}</p>
                    {it.variant.title && it.variant.title !== "Default Title" && (
                      <p className="text-xs text-neutral-500 font-sans mt-0.5">{it.variant.title}</p>
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-obsidian/20">
                        <button onClick={() => updateQty(it.variant.id, it.quantity - 1)} data-testid={`cart-decrement-${it.product.id}`} className="px-2 py-1 hover:bg-obsidian hover:text-bone transition-colors"><Minus className="w-3 h-3" /></button>
                        <span className="px-3 text-sm font-sans">{it.quantity}</span>
                        <button onClick={() => updateQty(it.variant.id, it.quantity + 1)} data-testid={`cart-increment-${it.product.id}`} className="px-2 py-1 hover:bg-obsidian hover:text-bone transition-colors"><Plus className="w-3 h-3" /></button>
                      </div>
                      <p className="font-serif text-lg">${(it.variant.price * it.quantity).toFixed(2)}</p>
                    </div>
                    <button onClick={() => removeItem(it.variant.id)} data-testid={`cart-remove-${it.product.id}`} className="text-[10px] tracking-widest uppercase text-neutral-500 mt-2 self-start underline underline-offset-4 hover:text-obsidian transition-colors">Remove</button>
                  </div>
                </div>
              ))}

              {items.length > 0 && upsell.length > 0 && (
                <div className="pt-6 border-t border-black/10" data-testid="cart-upsell">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-sans mb-4">
                    You might also <span className="italic text-obsidian normal-case tracking-normal">love</span>
                  </p>
                  <div className="space-y-3">
                    {upsell.slice(0, 3).map((p) => {
                      const variant = p.variants?.[0];
                      if (!variant) return null;
                      return (
                        <div
                          key={p.id}
                          className="flex items-center gap-3 group"
                          data-testid={`upsell-item-${p.id}`}
                        >
                          <div className="w-14 h-16 flex-shrink-0 bg-neutral-100 overflow-hidden">
                            {p.image && (
                              <img
                                src={p.image}
                                alt={p.name}
                                className="w-full h-full object-cover mix-blend-multiply"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 font-sans">
                              {p.brand}
                            </p>
                            <p className="font-serif text-sm leading-tight truncate">{p.name}</p>
                            <p className="font-serif text-sm mt-0.5">${variant.price?.toFixed?.(2) ?? variant.price}</p>
                          </div>
                          <button
                            onClick={() => addItem(p, variant, 1)}
                            data-testid={`upsell-add-${p.id}`}
                            className="text-[9px] tracking-[0.25em] uppercase font-sans border border-obsidian px-3 py-2 hover:bg-obsidian hover:text-bone transition-colors flex-shrink-0"
                          >
                            + Add
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-black/10 space-y-4">
              {(() => {
                let pendingCode = null;
                try { pendingCode = localStorage.getItem("doria_pending_discount"); } catch (e) { /* ignore */ }
                if (!pendingCode) return null;
                return (
                  <div className="flex items-center justify-between bg-[#0A0A0A]/10 px-3 py-2 text-xs font-sans" data-testid="cart-discount-hint">
                    <span className="text-obsidian">
                      Code <span className="font-serif italic text-[#0A0A0A]">{pendingCode}</span> will apply at checkout
                    </span>
                    <button
                      onClick={() => {
                        try { localStorage.removeItem("doria_pending_discount"); } catch (e) { /* ignore */ }
                        // Force re-render
                        setLoading((l) => l);
                        window.dispatchEvent(new Event("storage"));
                      }}
                      className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 hover:text-obsidian"
                      data-testid="cart-discount-remove"
                    >
                      Remove
                    </button>
                  </div>
                );
              })()}
              <div className="flex justify-between font-sans text-sm">
                <span className="text-neutral-500">Subtotal</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-neutral-500 font-sans">
                Shipping and taxes calculated at Shopify checkout.
              </p>
              <button
                onClick={goCheckout}
                disabled={items.length === 0 || loading}
                data-testid="cart-checkout-btn"
                className="w-full bg-obsidian text-bone py-4 text-[12px] tracking-[0.3em] uppercase font-sans font-medium hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : "Checkout on Shopify"}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
